import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios from 'axios';
import AddButton from 'components/AddButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import CheckBox from 'components/final-form/CheckBox';
import DatePicker from 'components/final-form/DatePicker';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  stringArrayToDropdownOptionArray
} from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import TextArea from 'components/final-form/TextArea';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import PraxFormScrollableContainer from 'components/layout/PraxFormScrollableContainer';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import UpsertProductItemForm from 'components/product/UpsertProductItemForm';
import { FormApi, FormState } from 'final-form';
import _ from 'lodash';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Modal, Table } from 'semantic-ui-react';
import { getAllBlockProcedures, getAllInterventions } from 'services/interventionServices';
import { getAllMandates } from 'services/mandateServices';
import { addProduct, editProduct, getProductPrice } from 'services/productServices';
import { getAllTariffs } from 'services/tariffServices';
import styled from 'styled-components';
import { InterventionBookingStatus, Urgency } from 'ts-types/api.enums';
import {
  BlockProcedureExtResponse,
  MandateDto,
  ProductDto,
  ProductItem,
  TariffDto,
  UpsertInterventionDto,
  UpsertProductDto
} from 'ts-types/api.types';
import { externalCodesWithProductSupport } from 'utils/enumUtils';
import { noop } from 'utils/functionUtils';
import { mustBePositiveNumber, required } from 'utils/validatorUtils';

const UpsertProductFormContent = styled(FormContent)`
  //max-width: 570px;

  .field {
    margin-bottom: 0.75rem;
  }

  .divider {
    margin-bottom: 1rem;
  }

  .tariff-error {
    color: darkred;
  }

  .tariff-block-procedures {
    font-style: italic;
  }

  .multifield-row {
    &.right {
      text-align: right;
    }

    .ui.checkbox {
      margin-right: 1.2rem;
    }
  }
`;

const ScrollableTableContainer = styled.div`
  height: 260px;
  overflow-y: auto;
  width: 100%;
  border: 1px solid rgba(34, 36, 38, .15);
  box-shadow: none;
  border-radius: .28571429rem;
  margin-bottom: 1em;

  & .ui.table {
    border: none;
  }

  & .ui.table thead tr:first-child > th {
    position: sticky !important;
    top: 0;
    z-index: 2;
    background: #fff;
  }

  & .ui.table tbody tr:last-child > td {
    border-bottom: 1px solid rgba(34, 36, 38, .15)
  }

  & .deleted-product {
    color: rgb(198, 198, 203);
  }
`;


interface Props extends MessageSourceProps,
    AuthConsumerRenderProps {

  onCancel: () => void,
  onSave: () => void,
  productId?: string,
  product?: UpsertProductDto,
  resourceOptions: DropdownOption[],
  articleGroupOptions: DropdownOption[]
}

interface State {
  pageDataLoaded: boolean,
  externalCodeOptions: DropdownOption[],
  interventionOptions: DropdownOption[],
  urgencyOptions: DropdownOption[],
  statusOptions: DropdownOption[],
  tariffMap: { [id: number]: TariffDto },
  tariffOptions: DropdownOption[],
  mandateMap: { [id: number]: MandateDto },
  mandateOptions: DropdownOption[],
  blockProcedureMap: { [id: number]: BlockProcedureExtResponse },

  openAddEditItemModal: boolean,
  productItemIx?: number,
  productItem?: ProductItem,

  interventionPriceInsurance?: number,
  interventionPricePatient?: number,

  errorMessage?: string,
}

class UpsertProductForm extends Component<Props, State> {

  cancelTokenSource = axios.CancelToken.source();

  private initialValues: Partial<UpsertProductDto>;

  constructor(props: Props) {
    super(props);

    const {productId, product, getMessage} = this.props;

    const urgencyOptions = stringArrayToDropdownOptionArray(
        Object.values(Urgency), props.getMessage, "enum.urgency");

    const supportedExternalCodes = externalCodesWithProductSupport();
    const externalCodeOptions = stringArrayToDropdownOptionArray(
        supportedExternalCodes, getMessage, "enum.InterventionExternalCode");

    const statusOptions = stringArrayToDropdownOptionArray(
        Object.values(InterventionBookingStatus), props.getMessage, "enum.interventionBookingStatus");

    this.initialValues = {
      productItems: []
    };

    if (productId) {
      this.initialValues = {
        ...product
      };
    }

    this.state = {
      pageDataLoaded: false,
      externalCodeOptions,
      interventionOptions: [],
      urgencyOptions,
      statusOptions,
      tariffMap: [],
      tariffOptions: [],
      mandateMap: [],
      mandateOptions: [],
      blockProcedureMap: [],
      openAddEditItemModal: false
    }
    this.loadPageData();
  }

  loadPageData = async () => {
    try {
      const tariffs = await getAllTariffs(this.cancelTokenSource);
      const mandates = await getAllMandates(this.cancelTokenSource);
      const blockProcedureMap: { [id: number]: BlockProcedureExtResponse } =
          await getAllBlockProcedures(this.cancelTokenSource);

      const tariffMap = _.keyBy(tariffs, "id");
      const tariffOptions = mapToDropdownOptionArray(tariffs, "description");

      const mandateMap = _.keyBy(mandates, "id");
      const mandateOptions = mapToDropdownOptionArray(mandates, "name");

      const interventions = await getAllInterventions(this.cancelTokenSource);
      const interventionOptions = mapToDropdownOptionArray(interventions, "description");

      this.setState({
        tariffMap,
        tariffOptions,
        mandateMap,
        mandateOptions,
        interventionOptions,
        blockProcedureMap
      });
    } finally {
      this.setState({
        pageDataLoaded: true
      });
    }
  }

  openAddEditItemModal = (form: FormApi, itemIx?: number) => {
    let productItemIx = itemIx;

    const formValues = form.getState().values;
    const productItems = formValues.productItems;

    this.setState({
      openAddEditItemModal: true
    });

    if (productItemIx === 0 || (productItemIx && productItemIx > 0)) {
      const productItem = productItems[productItemIx];

      this.setState({
        productItemIx: productItemIx,
        productItem: productItem
      });
    } else {
      const items: Partial<ProductItem>[] = [...formValues.productItems];
      items.push({
        id: "",
        productId: formValues.id,
        mandateId: undefined,
        tariffId: undefined,
        procedureGroups: [],
        cashOptions: false
      });

      form.change("productItems", items);

      productItemIx = items.length - 1;
      this.setState({
        productItemIx
      })
    }
  }

  hideAddEditItemModal = () => {
    this.setState({
      productItemIx: undefined,
      openAddEditItemModal: false,
      productItem: undefined
    });
  }

  onSaveItem = (form: FormApi) => {
    this.hideAddEditItemModal();
    setTimeout(() => {
      const productItems = [...form.getState().values.productItems];
      form.change("productItems", productItems);
    }, 10);
  }

  onCancelAddEditItem = (form: FormApi) => {
    const {productItem, productItemIx} = this.state;

    const productItems = [...form.getState().values.productItems];

    if (productItem && (productItemIx === 0 || (productItemIx && productItemIx > 0))) {
      productItems[productItemIx] = productItem;
    } else {
      productItems.pop();
    }

    form.change("productItems", productItems);

    this.hideAddEditItemModal();
  }

  deleteProductItem = (form: FormApi, productItemIx: number) => {
    const productItems = [...form.getState().values.productItems];
    if (productItems[productItemIx]) {
      productItems.splice(productItemIx, 1);
      form.change("productItems", productItems);
    }
  }

  onFormChange = (formState: FormState) => {
    const {openAddEditItemModal} = this.state;
    const values = formState.values;

    if (values
        && values.id
        && values.fromDate
        && values.externalCode
        && values.productItems.length > 0
        && !openAddEditItemModal
    ) {

      const product: Partial<ProductDto> = {
        id: values.id,
        fromDate: values.fromDate,
        externalCode: values.externalCode,
        resourceIds: values.resourceIds,
        productItems: values.productItems,
        articleGroupIds: values.articleGroupIds
      }

      getProductPrice(product, this.cancelTokenSource)
          .then(result => {
            const privateTariffInvoices = result.invoices.filter(inv => inv.privateTariff);
            if (privateTariffInvoices) {
              const totalAmountPrivateTariff = privateTariffInvoices.reduce(
                  (total, invoice) => total + invoice.amount, 0);

              this.setState({interventionPricePatient: totalAmountPrivateTariff});
            }

            const insuranceInvoices = result.invoices.filter(inv => !inv.privateTariff);
            if (insuranceInvoices) {
              const totalAmountInsurance = insuranceInvoices.reduce(
                  (total, invoice) => total + invoice.amount, 0);

              this.setState({interventionPriceInsurance: totalAmountInsurance});
            }
          })
          .catch(noop);
    }
  }


  handleSubmit = async (values: Partial<UpsertProductDto>) => {

    const {currentUser} = this.props;

    let saveValues: Partial<UpsertInterventionDto> = {...values};

    // const mandateId = currentUser ? currentUser.userConfig.mandateId : 1;

    const {productId, onSave} = this.props;
    const cancelTokenSource = this.cancelTokenSource;

    if (productId) {
      await editProduct(productId, saveValues, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => this.handleError(e.response.data))
    } else {
      await addProduct(saveValues, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => this.handleError(e.response.data))
    }
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [];

    const violations: Array<any> = error.violations;
    let knowErrorFound = false;
    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          knowErrorFound = true;
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    }

    if (!knowErrorFound) {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {
    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={this.initialValues}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting, values, form}: FormRenderProps): React.ReactNode => {

    const {productId, resourceOptions, articleGroupOptions, getMessage, onCancel} = this.props;
    const {
      interventionPriceInsurance,
      interventionPricePatient,
      externalCodeOptions,
      interventionOptions,
      urgencyOptions,
      statusOptions,
      errorMessage,
      openAddEditItemModal
    } = this.state;

    const disabledProdKey = !!productId;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <UpsertProductFormContent>
              <PraxFormScrollableContainer>
                {errorMessage
                    && (
                        <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                          {errorMessage}
                        </StyledErrorMessage>
                    )
                }
                <PraxFormGrid>
                  <Grid.Row>
                    <Grid.Column width={8}>
                      <div className="multifield-row">
                        <Field
                            name="systemProduct"
                            component={CheckBox}
                            label={`${getMessage("product.tableHeader.systemProduct")}`}
                            toggle
                        />
                        <Field
                            name="selfService"
                            component={CheckBox}
                            label={`${getMessage("product.tableHeader.selfService")}`}
                            toggle
                        />
                      </div>
                    </Grid.Column>
                    <Grid.Column width={8}>
                      <div className="multifield-row right">
                        <Field
                            name="active"
                            component={CheckBox}
                            label={`${getMessage("common.active")}`}
                            toggle
                        />
                      </div>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={9}>
                      <label htmlFor="id">{getMessage("product.form.prodKey")}</label>
                      <Field
                          fluid
                          id="id"
                          name="id"
                          component={Input}
                          validate={required}
                          disabled={disabledProdKey}
                          autoFocus
                      />
                      <label htmlFor="externalCode">{getMessage("product.tableHeader.interventionType")}</label>
                      <Field
                          fluid
                          id="externalCode"
                          name="externalCode"
                          component={Select}
                          options={externalCodeOptions}
                          validate={required}
                      />
                      <label htmlFor="externalCode">{getMessage("product.tableHeader.intervention")}</label>
                      <Field
                          fluid
                          id="interventionId"
                          name="interventionId"
                          component={Select}
                          options={interventionOptions}
                          clearable
                          // validate={required}
                      />
                      <label htmlFor="description">{getMessage("product.form.description")}</label>
                      <Field
                          fluid
                          id="description"
                          name="description"
                          component={Input}
                          validate={required}
                      />
                      <label htmlFor="descriptionEn">{getMessage("product.form.descriptionEn")}</label>
                      <Field
                          fluid
                          id="descriptionEn"
                          name="descriptionEn"
                          component={Input}
                      />
                      <label htmlFor="descriptionFr">{getMessage("product.form.descriptionFr")}</label>
                      <Field
                          fluid
                          id="descriptionFr"
                          name="descriptionFr"
                          component={Input}
                      />
                      <label htmlFor="descriptionIt">{getMessage("product.form.descriptionIt")}</label>
                      <Field
                          fluid
                          id="descriptionIt"
                          name="descriptionIt"
                          component={Input}
                      />
                      <label htmlFor="comments">{getMessage("product.form.comments")}</label>
                      <Field
                          id="comments"
                          name="comments"
                          component={TextArea}
                          rows={2}
                          placeholder={getMessage("product.form.comments")}
                      />
                      <label htmlFor="commentsEn">{getMessage("product.form.commentsEn")}</label>
                      <Field
                          id="commentsEn"
                          name="commentsEn"
                          component={TextArea}
                          rows={2}
                          placeholder={getMessage("product.form.commentsEn")}
                      />
                      <label htmlFor="commentsFr">{getMessage("product.form.commentsFr")}</label>
                      <Field
                          id="commentsFr"
                          name="commentsFr"
                          component={TextArea}
                          rows={2}
                          placeholder={getMessage("product.form.commentsFr")}
                      />
                      <label htmlFor="commentsIt">{getMessage("product.form.commentsIt")}</label>
                      <Field
                          id="commentsIt"
                          name="commentsIt"
                          component={TextArea}
                          rows={2}
                          placeholder={getMessage("product.form.commentsIt")}
                      />
                      <label htmlFor="fromDate">{getMessage("product.tableHeader.fromDate")}</label>
                      <Field
                          id="fromDate"
                          name="fromDate"
                          component={DatePicker}
                          validate={required}
                          fluid
                      />
                      <label htmlFor="toDate">{getMessage("product.tableHeader.toDate")}</label>
                      <Field
                          id="toDate"
                          name="toDate"
                          component={DatePicker}
                          validate={required}
                          fluid
                      />
                      <label htmlFor="resourceIds">{getMessage("product.tableHeader.location")}</label>
                      <Field
                          id="resourceIds"
                          label={`${getMessage("product.tableHeader.location")}`}
                          name="resourceIds"
                          component={Select}
                          options={resourceOptions}
                          fluid
                          clearable
                          search
                          multiple
                      />
                      <label htmlFor="articleGroupIds">{getMessage("product.tableHeader.articleGroup")}</label>
                      <Field
                          id="articleGroupIds"
                          label={`${getMessage("product.tableHeader.articleGroup")}`}
                          name="articleGroupIds"
                          component={Select}
                          options={articleGroupOptions}
                          fluid
                          search
                          multiple
                      />
                      <label htmlFor="resultAfter">{getMessage("product.form.resultAfter")}</label>
                      <Field
                          id="resultAfter"
                          name="resultAfter"
                          component={Input}
                          validate={mustBePositiveNumber}
                          fluid
                      />
                      <label htmlFor="urgency">{getMessage("product.tableHeader.urgency")}</label>
                      <Field
                          id="urgency"
                          name="urgency"
                          component={Select}
                          options={urgencyOptions}
                          validate={required}
                          fluid
                      />
                      <label htmlFor="status">{getMessage("product.tableHeader.statusAfterCheckIn")}</label>
                      <Field
                          id="statusAfterCheckIn"
                          name="statusAfterCheckIn"
                          component={Select}
                          options={statusOptions}
                          validate={required}
                          fluid
                      />
                      <div className="multifield-row">
                        <Field
                            name="generateCertificate"
                            component={CheckBox}
                            label={`${getMessage("product.tableHeader.certificate")}`}
                        />
                        <Field
                            name="generateReport"
                            component={CheckBox}
                            label={`${getMessage("product.tableHeader.report")}`}
                        />
                      </div>
                      <Field
                          name="freeProduct"
                          component={CheckBox}
                          label={`${getMessage("product.tableHeader.freeProduct")}`}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  {!values.freeProduct &&
                      <Grid.Row>
                        <Grid.Column width={16}>
                          {this.renderProductItemsTable(values, form)}
                          <AddButton buttonMessage={getMessage("button.add")}
                                     onClick={() => this.openAddEditItemModal(form)}
                                     type="button"
                          />
                        </Grid.Column>
                      </Grid.Row>
                  }
                  {!values.freeProduct
                      ? <Grid.Row>
                        <Grid.Column width={3}>
                          {getMessage("anamnesis.form.amountInsurance")}:
                        </Grid.Column>
                        <Grid.Column width={2}>
                          {
                              interventionPriceInsurance !== undefined &&
                              <span>{interventionPriceInsurance}&nbsp;CHF</span>
                          }
                        </Grid.Column>

                        <Grid.Column width={3}>
                          {getMessage("anamnesis.form.amountPatient")}:
                        </Grid.Column>
                        <Grid.Column width={2}>
                          {
                              interventionPriceInsurance !== undefined &&
                              <span>{interventionPricePatient}&nbsp;CHF</span>
                          }
                        </Grid.Column>
                      </Grid.Row>
                      : <Grid.Row>
                        <Grid.Column>
                          <span style={{fontStyle: "italic", color: "#0096FF"}}>
                            {getMessage("product.freeProduct.message")}
                          </span>
                        </Grid.Column>
                      </Grid.Row>
                  }
                </PraxFormGrid>
              </PraxFormScrollableContainer>
            </UpsertProductFormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
          {openAddEditItemModal && this.renderUpsertProductItemModal(form)}
          <FormSpy
              subscription={{values: true, hasValidationErrors: true}}
              onChange={this.onFormChange}
          />
        </form>
    );
  };

  renderProductItemsTable = (values: any, form: FormApi): React.ReactNode => {
    const {getMessage} = this.props;
    const {mandateMap, tariffMap} = this.state;

    const productItems: ProductItem[] = values.productItems;

    return (
        <ScrollableTableContainer>
          <Table celled size="small">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={2}>{getMessage('product.form.item.lineItem')}</Table.HeaderCell>
                <Table.HeaderCell width={2}>{getMessage('product.form.item.invoiceMandate')}</Table.HeaderCell>
                <Table.HeaderCell width={2}>{getMessage('product.form.item.tariff')}</Table.HeaderCell>
                <Table.HeaderCell width={2}>{getMessage('product.form.item.procedureGroups')}</Table.HeaderCell>
                <Table.HeaderCell width={2}>{getMessage('product.form.item.cashOptions')}</Table.HeaderCell>
                <Table.HeaderCell width={2}>{getMessage('actions')}</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {productItems.map((productItem, index) => {
                return (
                    <Table.Row key={index}>
                      <Table.Cell width={2}>
                        {productItem.id ? productItem.id : ""}
                      </Table.Cell>
                      <Table.Cell width={2}>
                        {mandateMap[productItem.mandateId] ? mandateMap[productItem.mandateId].name : ""}
                      </Table.Cell>
                      <Table.Cell width={2}>
                        {tariffMap[productItem.tariffId] ? tariffMap[productItem.tariffId].description : ""}
                      </Table.Cell>
                      <Table.Cell width={2}>
                        {productItem.procedureGroups && productItem.procedureGroups.map(pg => <div key={pg}>{pg}</div>)}
                      </Table.Cell>
                      <Table.Cell width={2}>
                        {productItem.cashOptions
                            ? getMessage("product.form.item.allPaymentOptions")
                            : getMessage("invoice")
                        }
                      </Table.Cell>
                      <Table.Cell width={2}>
                        <EditActionButton
                            hoverMessage={getMessage('msgEdit')}
                            onConfirm={() => this.openAddEditItemModal(form, index)}
                        />
                        <DeleteActionButton
                            hoverMessage={getMessage('msgDelete')}
                            onConfirm={() => this.deleteProductItem(form, index)}
                        />
                      </Table.Cell>
                    </Table.Row>
                )
              })
              }
            </Table.Body>
          </Table>
        </ScrollableTableContainer>
    );
  }

  renderUpsertProductItemModal = (form: FormApi): React.ReactNode => {

    const {getMessage} = this.props;
    const {
      productItemIx,
      tariffOptions,
      mandateOptions,
      openAddEditItemModal,
      blockProcedureMap
    } = this.state;

    return (
        <Modal
            open={openAddEditItemModal}
            onClose={this.hideAddEditItemModal}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("product.form.title")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertProductItemForm
                onCancel={this.onCancelAddEditItem}
                onSave={() => this.onSaveItem(form)}
                form={form}
                interventionId={form.getState().values.interventionId}
                productItemIx={productItemIx}
                mandateOptions={mandateOptions}
                tariffOptions={tariffOptions}
                blockProcedureMap={blockProcedureMap}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };

}

export default withMessages(withAuthContext(UpsertProductForm));
